import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { useSiteStatus } from '../../context/SiteStatusContext';
import AlertMessage from '../../components/ui/AlertMessage';
import CopyButton from '../../components/ui/CopyButton';
import SiteIcon from '../../components/ui/SiteIcon';
import { EmptyState } from '../../components/ui/PanelCard';
import { Kicker, Rows, Row, RowNote, Chip, Countdown, KV, LicenceTag, utcStamp } from '../../components/account/AccountUi';

/*
 * Dashboard › Licence & loader (a bot oldala, bot.mortisengine.com; 2026-10-06-tól a Codex admin „Bot” részében
 * volt, 10-07 óta itt): a saját licenc a HWID-del, a launcher letöltése (2026-10-21 óta; előtte a loader zip-je) és
 * az eszköz. A kulcs-aktiválás 10-06 este kikerült: a vásárlás kulcs nélkül ír jóvá
 * („nem kell már sehol kulcsot aktiválni”, backend services/accessGrant.js).
 */

export default function BotLicenceTab() {
  const { launcherVersion } = useSiteStatus();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [siteStatus, setSiteStatus] = useState('working');

  const [dlLoading, setDlLoading] = useState(false);
  const [showHwid, setShowHwid] = useState(false);

  useEffect(() => {
    api.get('/user/profile').then(res => setProfile(res.data)).catch(() => setProfile(false));
    api.get('/owner/status').then(res => setSiteStatus(res.data.status)).catch(() => {});
  }, []);

  const downloadLauncher = async () => {
    setDlLoading(true);
    try {
      const res = await api.get('/user/download-launcher', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'MortisLauncher.exe';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // 503: épp most megy ki egy új launcher (a Panel kiadása félúton) — percen belül újra jó
      toast.error(err?.response?.status === 503
        ? 'A new launcher is being published. Try again in a minute.'
        : 'Download failed. Please try again.');
    } finally {
      setDlLoading(false);
    }
  };

  if (profile === null) {
    return (
      <div className="flex flex-col gap-6" aria-busy="true">
        <div className="skeleton h-40 w-full" />
        <div className="grid gap-6 min-[900px]:grid-cols-2"><div className="skeleton h-28" /><div className="skeleton h-28" /></div>
      </div>
    );
  }
  if (profile === false) {
    return <AlertMessage className="" msg={{ type: 'error', text: 'Your licence could not be loaded. Refresh the page.' }} />;
  }

  const sub = profile.subscription;
  const hwid = profile.user?.hwid;
  const isUpdating = siteStatus === 'updating';
  const hasActiveSub = sub && (sub.lifetime || (new Date(sub.expires_at) > new Date() && !sub.is_frozen));

  return (
    <div className="flex flex-col gap-10">
      <section aria-labelledby="b-sub">
        <Kicker id="b-sub" aside="times in UTC">Licence</Kicker>
        {sub ? (
          <div className="card px-5 py-5 sm:px-6 sm:py-[22px] grid gap-x-10 gap-y-7 min-[900px]:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
            <div className="flex flex-col gap-3.5 min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <b className="text-[17px] font-semibold text-gray-50">{sub.lifetime ? 'MortisEngine Bot' : sub.product_name}</b>
                <Chip tone="acc">{sub.plan_label}</Chip>
                <LicenceTag sub={sub} updating={isUpdating} />
              </div>
              {sub.lifetime ? (
                <p className="font-mono text-[26px] font-semibold tracking-[-0.02em] text-brand-400">Never expires</p>
              ) : (
                <Countdown expiresAt={sub.expires_at} frozen={!!sub.is_frozen} />
              )}
              {!!sub.is_frozen && (
                <p className="text-[13px] text-gray-500">Your time is paused. It continues when the licence is unfrozen.</p>
              )}
            </div>
            <dl className="m-0 min-w-0">
              <KV k="Plan">{sub.plan_label}</KV>
              <KV k="Locked to" mono>
                <span className="flex items-center gap-1 min-w-0">
                  <span className="truncate">{sub.hwid || hwid || '—'}</span>
                  {(sub.hwid || hwid) && <CopyButton value={sub.hwid || hwid} variant="icon" />}
                </span>
              </KV>
              <KV k="Expires" mono>{sub.lifetime ? 'Never' : utcStamp(sub.expires_at)}</KV>
            </dl>
          </div>
        ) : (
          <div className="card">
            <EmptyState
              icon={<><rect x="4" y="11" width="16" height="9" rx="2" /><path d="M8 11V7a4 4 0 118 0v4" /></>}
              title="No active licence"
              hint="Buying access on the Pricing page gives the licence too."
            />
          </div>
        )}
      </section>

      <div className="grid gap-x-6 gap-y-10 min-[900px]:grid-cols-2">
        <section aria-labelledby="b-launcher" className="min-w-0">
          <Kicker id="b-launcher">Launcher</Kicker>
          <Rows>
            <Row k="Mortis Launcher" mono
                 acts={(
                   <button type="button" onClick={downloadLauncher} disabled={dlLoading || !hasActiveSub}
                           className="btn-primary btn-sm">
                     <SiteIcon name="download" className="w-[15px] h-[15px]" />
                     {dlLoading ? 'Downloading…' : 'Download'}
                   </button>
                 )}>
              {launcherVersion ? `v${launcherVersion}` : '—'}
            </Row>
            <RowNote>
              {hasActiveSub
                ? <>Run <code className="code-inline">MortisLauncher.exe</code> as administrator, sign in with your MortisEngine app account,
                    then add your game accounts.</>
                : 'The download needs an active licence.'}
            </RowNote>
          </Rows>
        </section>

        <section aria-labelledby="b-device" className="min-w-0">
          <Kicker id="b-device">Device</Kicker>
          <Rows>
            <Row k="HWID" mono
                 acts={hwid && (
                   <button type="button" onClick={() => setShowHwid(v => !v)} aria-pressed={showHwid}
                           aria-label={showHwid ? 'Hide HWID' : 'Show HWID'} className="btn-icon w-8 h-8">
                     <SiteIcon name="eye" className="w-4 h-4" />
                   </button>
                 )}>
              <span className="block truncate" title={showHwid ? hwid : undefined}>
                {hwid ? (showHwid ? hwid : `••••••••${String(hwid).slice(-4)}`) : 'Not linked yet'}
              </span>
            </Row>
            <RowNote>
              {hwid
                ? 'Moving to a new PC? Ask for an HWID reset on Discord (Docs → HWID Issues).'
                : 'The launcher links this PC the first time you sign in with it.'}
            </RowNote>
          </Rows>
        </section>
      </div>

    </div>
  );
}
